import { useRef, useState, useEffect } from 'react';
import { useForm } from 'react-hook-form';
import { useTranslation } from 'react-i18next';
import * as Yup from 'yup';
import { yupResolver } from '@hookform/resolvers/yup';
import { useParams } from 'react-router-dom';

import config from 'data/config.json';
import {
  ALREADY_REGISTERED,
  SET_ACTIVITY,
  SET_ZONE,
  START_REGISTER
} from 'context/RegisterProvider/constants';
import { useRegisterStore } from 'context/RegisterProvider/hooks';
import { validateEmail } from 'services/validateEmail';
import Notice from 'UI/atoms/Notice';
import ReCaptcha from 'UI/atoms/ReCaptcha';
import Form from 'UI/molecules/Form';
import CardContent from 'UI/atoms/CardContent';
import Iframe from 'components/Iframe';

const Step1Register = () => {
  const { t } = useTranslation();
  const { locale } = useParams();
  const [validRecaptcha, setValidRecaptcha] = useState(false);
  const [registerState, registerDispatch] = useRegisterStore();
  const [loading, setLoading] = useState(false);
  const [mailIsValid, setMailIsValid] = useState(true);
  const recaptchaRef = useRef();
  const iframeUrl = process.env.REACT_APP_ENV !== 'prod'
    ? process.env.REACT_APP_COOKIE_DOMAIN
    : t('web.domain');

  const validationSchema = Yup.object().shape({
    email: Yup.string()
      .required(t('form.error.required'))
      .email(t('form.error.email.invalid'))
      .test('is-mail-valid', t('form.error.email.not-corporative'), () => mailIsValid)
  });

  const { register, handleSubmit, trigger, setValue, setError, watch, formState: { errors } } = useForm({
    resolver: yupResolver(validationSchema),
    mode: 'onSubmit',
    reValidateMode: 'onSubmit',
    defaultValues: {
      email: ''
    }
  });

  const emailField = watch('email');

  useEffect(() => {
    setValue('email', emailField.replace(/ /gi, ''));
    setMailIsValid(true);
  }, [emailField]);

  useEffect(() => {
    window.sessionStorage.removeItem('token');
    const zone = config.zones.find((zone) => zone.locale === locale);
    registerDispatch({ type: SET_ZONE, payload: zone.id });

    window.addEventListener('message', (e) => {
      if (e.origin === iframeUrl) {
        const msg = e.data;
        const { activityId } = msg;
        if (activityId && activityId.length > 0) {
          registerDispatch({ type: SET_ACTIVITY, payload: activityId });
        }
      }
    });
  }, []);

  useEffect(() => {
    const validateEmailField = async () => {
      await trigger();
    };
    if (!mailIsValid) {
      validateEmailField();
    }
  }, [mailIsValid]);

  const onSubmit = async (data) => {
    setLoading(true);
    const token = recaptchaRef.current.getValue();
    validateEmail({
      zone_id: registerState.zone,
      email: data.email,
      recaptcha: token,
    })
      .then((result) => {
        if (result.token) {
          window.sessionStorage.setItem('token', result.token);
        }
        if (result.isUsed) {
          registerDispatch({ type: ALREADY_REGISTERED, payload: data });
          return;
        }
        if (!result.isValid) {
          recaptchaRef.current.reset();
          setMailIsValid(false);
          return;
        }
        registerDispatch({ type: START_REGISTER, payload: data });
      })
      .catch(() => {
        setError('apiError', { message: t('api.error') });
        recaptchaRef.current.reset();
      })
      .finally(() => {
        setLoading(false);
      });
  };

  const onChangeRecaptcha = () => {
    const recaptchaValue = recaptchaRef.current.getValue();
    if (recaptchaValue) {
      setValidRecaptcha(true);
    }
  };

  const onExpiredRecaptcha = () => {
    setValidRecaptcha(false);
  };

  return (
    <CardContent sm={{ span: 10, offset: 1 }}>
      <div>
        <Notice color="blue" arrow>{t('form.step1.notice')}</Notice>
        <Form onSubmit={handleSubmit(onSubmit)}>
          <Form.Field>
            <Form.Label htmlFor="form_email">
              {t('form.step1.email.label')}
            </Form.Label>
            <Form.Control>
              <Form.Input
                disabled={loading}
                error={errors.email}
                id="form_email"
                name="email"
                {...register('email')}
                placeholder={t('form.step1.email.placeholder')}
              />
              {errors.email && <Form.Error>{errors.email?.message}</Form.Error>}
            </Form.Control>
          </Form.Field>

          <ReCaptcha
            sitekey={process.env.REACT_APP_RECAPTCHA_KEY}
            onChange={onChangeRecaptcha}
            onExpired={onExpiredRecaptcha}
            ref={recaptchaRef}
          />

          {errors.apiError && (
            <Notice color="red">
              <span
                dangerouslySetInnerHTML={{ __html: errors.apiError?.message }}
              ></span>
            </Notice>
          )}

          <Form.Button disabled={!validRecaptcha || loading}>
            {t('form.step1.button')}
            {loading && <Form.Loader />}
          </Form.Button>
          <Iframe
            id="ifr"
            source={`${iframeUrl}/push-cookie.html`}
            width={0}
            height={0}
          ></Iframe>
        </Form>
      </div>
    </CardContent>
  );
};

export default Step1Register;
